import { EducationLevel, SkillCategory } from './enums';

/** Indian mobile: 10 digits starting 6-9. Stored without the +91 prefix. */
export const PHONE_REGEX = /^[6-9]\d{9}$/;
export const AADHAAR_REGEX = /^\d{12}$/;
export const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;
/** GSTIN: 2-digit state code + PAN + entity code + 'Z' + checksum. */
export const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d]Z[A-Z\d]$/;
export const PINCODE_REGEX = /^[1-9]\d{5}$/;
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z\d]{6}$/;
export const UAN_REGEX = /^\d{12}$/;

export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;
/** Bulk import / bulk shortlist ceiling per request. */
export const MAX_BULK_SIZE = 1000;

export const MAX_UPLOAD_BYTES = {
  RESUME: 5 * 1024 * 1024,
  PHOTO: 2 * 1024 * 1024,
  DOCUMENT: 10 * 1024 * 1024,
} as const;

export const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  RESUME: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  PHOTO: ['image/jpeg', 'image/png', 'image/webp'],
  DOCUMENT: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'],
};

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland',
  'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
  'Ladakh', 'Lakshadweep', 'Puducherry',
] as const;

/**
 * Languages the UI ships in: English plus the ten most-spoken Indian languages.
 *
 * `label` is deliberately the endonym — someone who cannot read English cannot
 * find "Tamil" in a list, but they can find "தமிழ்".
 *
 * `dir` matters: Urdu is right-to-left, which is a layout concern rather than a
 * string swap. Anything positional has to use logical properties (ps-/pe-) or it
 * will render backwards for Urdu readers.
 *
 * `reviewed` records whether a native speaker has checked the translations. It
 * is false for everything but English until someone actually does that, because
 * appearing in this list is not the same as being finished — and wage, shift and
 * safety wording is where a confident wrong translation does real harm.
 */
export const UI_LANGUAGES = [
  { code: 'en', label: 'English', english: 'English', dir: 'ltr', reviewed: true },
  { code: 'hi', label: 'हिन्दी', english: 'Hindi', dir: 'ltr', reviewed: false },
  { code: 'bn', label: 'বাংলা', english: 'Bengali', dir: 'ltr', reviewed: false },
  { code: 'mr', label: 'मराठी', english: 'Marathi', dir: 'ltr', reviewed: false },
  { code: 'te', label: 'తెలుగు', english: 'Telugu', dir: 'ltr', reviewed: false },
  { code: 'ta', label: 'தமிழ்', english: 'Tamil', dir: 'ltr', reviewed: false },
  { code: 'gu', label: 'ગુજરાતી', english: 'Gujarati', dir: 'ltr', reviewed: false },
  { code: 'ur', label: 'اردو', english: 'Urdu', dir: 'rtl', reviewed: false },
  { code: 'kn', label: 'ಕನ್ನಡ', english: 'Kannada', dir: 'ltr', reviewed: false },
  { code: 'or', label: 'ଓଡ଼ିଆ', english: 'Odia', dir: 'ltr', reviewed: false },
  { code: 'ml', label: 'മലയാളം', english: 'Malayalam', dir: 'ltr', reviewed: false },
] as const;

export type UiLanguageCode = (typeof UI_LANGUAGES)[number]['code'];

export const UI_LANGUAGE_CODES = UI_LANGUAGES.map((l) => l.code) as readonly UiLanguageCode[];

export const DEFAULT_LANGUAGE: UiLanguageCode = 'en';

export const isUiLanguage = (value: unknown): value is UiLanguageCode =>
  typeof value === 'string' && (UI_LANGUAGE_CODES as readonly string[]).includes(value);

export const languageDirection = (code: string): 'ltr' | 'rtl' =>
  UI_LANGUAGES.find((l) => l.code === code)?.dir ?? 'ltr';

export const SPOKEN_LANGUAGES = [
  'English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi',
  'Gujarati', 'Bengali', 'Odia', 'Punjabi', 'Assamese', 'Urdu',
] as const;

/** NCVT trades — drives the course dropdown when EducationLevel = ITI. */
export const ITI_TRADES = [
  'Fitter', 'Turner', 'Machinist', 'Machinist (Grinder)', 'Welder',
  'Electrician', 'Electronics Mechanic', 'Instrument Mechanic',
  'Mechanic (Motor Vehicle)', 'Mechanic (Diesel)', 'Tool & Die Maker',
  'Draughtsman (Mechanical)', 'Draughtsman (Civil)', 'Sheet Metal Worker',
  'Plumber', 'Wireman', 'Refrigeration & Air Conditioning Technician',
  'COPA (Computer Operator & Programming Assistant)', 'Painter (General)',
  'Foundryman', 'Moulder', 'Carpenter', 'Mechanic (Machine Tool Maintenance)',
] as const;

export const DIPLOMA_BRANCHES = [
  'Mechanical Engineering', 'Electrical & Electronics Engineering',
  'Electronics & Communication Engineering', 'Civil Engineering',
  'Computer Engineering', 'Automobile Engineering', 'Mechatronics',
  'Instrumentation & Control', 'Production Engineering', 'Chemical Engineering',
  'Textile Technology', 'Printing Technology',
] as const;

export const DEGREE_BRANCHES = [
  'Mechanical Engineering', 'Electrical & Electronics Engineering',
  'Electronics & Communication Engineering', 'Computer Science & Engineering',
  'Information Technology', 'Civil Engineering', 'Automobile Engineering',
  'Mechatronics', 'Instrumentation Engineering', 'Production Engineering',
  'Industrial Engineering', 'Chemical Engineering', 'Aeronautical Engineering',
  'B.Sc Physics', 'B.Sc Chemistry', 'B.Sc Mathematics', 'B.Sc Computer Science',
  'B.Com', 'B.A English', 'BBA', 'BCA', 'Other',
] as const;

/** Courses offered per level — powers the dependent dropdown in the profile form. */
export const COURSES_BY_LEVEL: Partial<Record<EducationLevel, readonly string[]>> = {
  ITI: ITI_TRADES,
  DIPLOMA: DIPLOMA_BRANCHES,
  ENGINEERING_UG: DEGREE_BRANCHES,
  UNDERGRADUATE: DEGREE_BRANCHES,
};

/** Seeded into the Skill table. Category → skill names. */
export const SEED_SKILLS: Record<SkillCategory, string[]> = {
  CNC: ['CNC Operator', 'CNC Programmer', 'CNC Turning', 'CNC Milling', 'VMC Operator', 'HMC Operator', 'Fanuc Control', 'Siemens Sinumerik', 'Mazak', 'G-Code / M-Code'],
  SMT: ['SMT Operator', 'SMT Line Setup', 'Pick & Place Machine', 'Reflow Oven', 'Screen Printer', 'AOI Operator', 'SPI Operator', 'Wave Soldering', 'PCB Assembly', 'Feeder Setup'],
  WELDING: ['Arc Welding', 'MIG Welding', 'TIG Welding', 'Spot Welding', 'Gas Welding', 'Robotic Welding', 'Welding Inspection', 'Brazing'],
  ASSEMBLY: ['Line Assembly', 'Sub-Assembly', 'Mechanical Fitting', 'Wire Harness', 'Torque Tools', 'Poka-Yoke', 'Final Assembly', 'Mobile Phone Assembly'],
  QUALITY: ['Quality Inspection', 'IQC', 'IPQC', 'OQC', 'Vernier Caliper', 'Micrometer', 'Height Gauge', 'CMM Operator', 'SPC', '5S', 'Six Sigma', 'ISO 9001', 'IATF 16949', '8D / RCA', 'Gauge R&R'],
  MAINTENANCE: ['Preventive Maintenance', 'Breakdown Maintenance', 'TPM', 'Hydraulics', 'Pneumatics', 'PLC Maintenance', 'Machine Overhauling', 'Conveyor Maintenance'],
  PRODUCTION: ['Production Operator', 'Line Leader', 'Shift Supervisor', 'Production Planning', 'OEE Tracking', 'Kaizen', 'Lean Manufacturing'],
  ELECTRICAL: ['Panel Wiring', 'Motor Winding', 'Transformer Maintenance', 'DG Set Operation', 'HT/LT Panel', 'Cable Laying', 'Electrical Troubleshooting'],
  ELECTRONICS: ['PCB Soldering', 'Component Testing', 'Multimeter', 'Oscilloscope', 'Rework Station', 'Firmware Flashing', 'Functional Testing'],
  MECHANICAL: ['Lathe Operator', 'Milling Machine', 'Drilling Machine', 'Grinding Machine', 'Surface Grinder', 'Press Machine Operator', 'Blueprint Reading', 'GD&T', 'AutoCAD', 'SolidWorks', 'CATIA'],
  TOOL_ROOM: ['Die Maintenance', 'Mould Maintenance', 'Jig & Fixture', 'EDM', 'Wire Cut EDM', 'Tool Setting'],
  PAINT_SHOP: ['Powder Coating', 'Spray Painting', 'Phosphating', 'Paint Inspection', 'ED Coating'],
  MOULDING: ['Injection Moulding', 'Blow Moulding', 'Compression Moulding', 'Die Casting', 'Mould Setting'],
  PACKING: ['Packing Operator', 'Labelling', 'Shrink Wrapping', 'Carton Packing', 'FG Handling'],
  WAREHOUSE: ['Store Keeper', 'Material Handling', 'Forklift Operator', 'Stacker Operator', 'Inventory Control', 'SAP MM', 'Kanban', 'Dispatch'],
  SAFETY: ['EHS', 'Fire Safety', 'First Aid', 'PPE Compliance', 'Work Permit System', 'Safety Audit'],
  COMPUTER: ['MS Excel', 'MS Word', 'Data Entry', 'SAP', 'ERP', 'Tally', 'Basic Computer'],
  SOFT_SKILL: ['Team Leading', 'Communication', 'Shop Floor Documentation', 'Training & Induction'],
  OTHER: [],
};

/** Seeded into JobCategory. */
export const SEED_JOB_CATEGORIES = [
  { name: 'CNC / Machine Shop', slug: 'cnc-machine-shop' },
  { name: 'SMT / Electronics Manufacturing', slug: 'smt-electronics' },
  { name: 'Welding & Fabrication', slug: 'welding-fabrication' },
  { name: 'Assembly Line', slug: 'assembly-line' },
  { name: 'Quality Control', slug: 'quality-control' },
  { name: 'Maintenance & Utilities', slug: 'maintenance-utilities' },
  { name: 'Production & Operations', slug: 'production-operations' },
  { name: 'Tool Room', slug: 'tool-room' },
  { name: 'Paint Shop', slug: 'paint-shop' },
  { name: 'Moulding & Die Casting', slug: 'moulding-die-casting' },
  { name: 'Packing & Dispatch', slug: 'packing-dispatch' },
  { name: 'Stores & Warehouse', slug: 'stores-warehouse' },
  { name: 'Safety / EHS', slug: 'safety-ehs' },
  { name: 'Helper / Unskilled', slug: 'helper-unskilled' },
  { name: 'Supervisor / Line Leader', slug: 'supervisor-line-leader' },
  { name: 'Engineering / Technical', slug: 'engineering-technical' },
  { name: 'Office / Admin', slug: 'office-admin' },
] as const;

/** Perks a manufacturing employer typically offers — rendered as chips on the job card. */
export const JOB_BENEFITS = [
  'FOOD', 'ACCOMMODATION', 'TRANSPORT', 'PF', 'ESI', 'BONUS', 'OVERTIME',
  'UNIFORM', 'MEDICAL_INSURANCE', 'ATTENDANCE_INCENTIVE', 'NIGHT_ALLOWANCE',
  'SKILL_ALLOWANCE',
] as const;
export type JobBenefit = (typeof JOB_BENEFITS)[number];

export const BENEFIT_LABEL: Record<JobBenefit, string> = {
  FOOD: 'Food',
  ACCOMMODATION: 'Accommodation',
  TRANSPORT: 'Transport',
  PF: 'PF',
  ESI: 'ESI',
  BONUS: 'Bonus',
  OVERTIME: 'Overtime',
  UNIFORM: 'Uniform',
  MEDICAL_INSURANCE: 'Medical insurance',
  ATTENDANCE_INCENTIVE: 'Attendance incentive',
  NIGHT_ALLOWANCE: 'Night allowance',
  SKILL_ALLOWANCE: 'Skill allowance',
};

/**
 * The contracting party.
 *
 * "NxtHire Workforce Solutions" is the brand the site presents; the entity that
 * signs agreements, holds the licences and raises invoices is the private
 * limited company. Anything with legal or financial weight — a tax invoice, a
 * contract, the site footer — must carry the legal name, so both take it from
 * here rather than each hard-coding their own copy.
 *
 * A tax invoice cannot be issued without a GSTIN, and the billing module
 * refuses to issue one while it is blank rather than printing a placeholder.
 * `BILLING_GSTIN` in the API environment overrides it for a test database.
 */
export const SUPPLIER = {
  legalName: 'NxtTecX Infotech Private Limited',
  brandName: 'NxtHire Workforce Solutions',
  /**
   * The header lockup stacks the name over the descriptor, because the full
   * brand does not fit a 240px rail on one line. Splitting it here keeps the
   * shells from slicing `brandName` apart at render time.
   */
  brandShort: 'NxtHire',
  brandDescriptor: 'Workforce Solutions',
  tagline: 'Reliable workforce for manufacturing excellence',
  addressLine1: 'No.1, Kambar Street, 4th Cross Street',
  addressLine2: 'Sriperumbudur',
  city: 'Kanchipuram',
  state: 'Tamil Nadu',
  pincode: '602105',
  country: 'India',
  /**
   * The payroll desk. Printed on a payslip so a worker who disputes a figure has
   * somewhere to call — the footer tells them to raise it before the next pay
   * date, which is useless without a number.
   */
  phone: '7303857111',
  /**
   * Public contact numbers, shown in the site footer and in the fraud warning.
   *
   * Distinct from `phone` above, which is the payroll desk printed on payslips.
   * Two numbers rather than one because the warning tells a candidate being
   * asked for money to report it — advice worth nothing if the single line is
   * engaged.
   *
   * Held here rather than in the component so they cannot drift from what an
   * invoice prints, which is exactly what had happened.
   */
  supportPhone: '8754121529',
  altPhone: '9342346559',
  gstin: '33AALCN8025E1ZK',
  // The PAN is embedded in the GSTIN: state code (33) + PAN + entity + Z + check.
  pan: 'AALCN8025E',
  cin: 'U62091TN2026PTC194752',
} as const;

/** `9342346559` -> `+91 93423 46559`, the form Indian readers expect. */
export const formatIndianPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return raw;
  return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
};

/** `tel:` target — no spaces, country code included. */
export const telHref = (raw: string): string => `tel:+91${raw.replace(/\D/g, '').slice(-10)}`;

/** The supplier's full postal address, as printed on an invoice. */
export const supplierAddressLines = (): string[] =>
  [
    SUPPLIER.addressLine1,
    SUPPLIER.addressLine2,
    `${SUPPLIER.city} – ${SUPPLIER.pincode}`,
    `${SUPPLIER.state}, ${SUPPLIER.country}`,
  ].filter(Boolean);
